import {Box, Button, Grid} from "@mui/material";
import {DataGrid, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer} from "@mui/x-data-grid";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import {REP_TYPES} from "../../constants";
import {useBusinessUnitCodes} from "../../hooks/useBusinessUnitCodes";
import {useFacilities} from "../../hooks/useFacilities";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {disableSortingAndMenu} from "../../util/FilterOperatorsUtil";
import FacilityModal from "./FacilityModal";
import FacilitiesFilter from "./FacilitiesFilter";
import MyBooleanMark from "../../components/MyBooleanMark";

const defaultColumnWidths = {
    id: 85,
    name: 137,
    businessUnitCode: 228,
    users: 142,
    city: 210,
    address: 297,
    state: 134,
    postalCode: 148,
    timeZoneId: 129,
    createdDate: 209,
    updatedDate: 203,
    golferOrderActionTypeName: 188,
};
const FacilitiesTable = () => {
    const [filter, setFilter] = useState({
        businessUnitCode: "",
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    useEffect(() => {
        if (!routes.find((route) => route.key === "facilities").visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [facilitiesColumnWidths, setFacilitiesColumnWidths] = useLocalStorage("facilitiesColumnWidths", {});

    const columns = useMemo(() => {
        let header = [
            {
                field: "id",
                headerName: "ID",
                width: facilitiesColumnWidths.id ?? defaultColumnWidths.id,
                filterable: false,
            },
            {
                field: "name",
                headerName: "NAME",
                width: facilitiesColumnWidths.name ?? defaultColumnWidths.name,
                filterable: false,
            },
            {
                field: "businessUnitCode",
                headerName: "BUSINESS UNIT",
                width: facilitiesColumnWidths.businessUnitCode ?? defaultColumnWidths.businessUnitCode,
                filterable: false,
            },
            {
                field: "bookable",
                headerName: "BOOKABLE",
                width: facilitiesColumnWidths.bookable ?? defaultColumnWidths.bookable,
                filterable: false,
                renderCell: (params) => <MyBooleanMark value={params.value} />,
            },
            {
                field: "country",
                headerName: "COUNTRY",
                width: facilitiesColumnWidths.country ?? defaultColumnWidths.country,
                filterable: false,
            },
            {
                field: "city",
                headerName: "CITY",
                width: facilitiesColumnWidths.city ?? defaultColumnWidths.city,
                filterable: false,
            },
            {
                field: "address",
                headerName: "ADDRESS 1, ADDRESS 2",
                width: facilitiesColumnWidths.address ?? defaultColumnWidths.address,
                filterable: false,
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.localeCompare(v2);
                },
            },
            {
                field: "state",
                headerName: "STATE",
                width: facilitiesColumnWidths.state ?? defaultColumnWidths.state,
                filterable: false,
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.localeCompare(v2);
                },
            },
            {
                field: "postalCode",
                headerName: "POSTAL CODE",
                width: facilitiesColumnWidths.postalCode ?? defaultColumnWidths.postalCode,
                filterable: false,
                sortComparator: (v1, v2) => {
                    if (v1 === null) return 1;
                    if (v2 === null) return -1;
                    return v1.localeCompare(v2);
                },
            },
            {
                field: "golferOrderActionTypeName",
                headerName: "GOLFER ORDER ACTION TYPE NAME",
                width:
                    facilitiesColumnWidths.golferOrderActionTypeName ?? defaultColumnWidths.golferOrderActionTypeName,
                filterable: false,
            },
            {
                field: "createdDate",
                headerName: "CREATED DATE",
                width: facilitiesColumnWidths.createdDate ?? defaultColumnWidths.createdDate,
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    } else {
                        return "";
                    }
                },
            },
            {
                field: "updatedDate",
                headerName: "UPDATED DATE",
                width: facilitiesColumnWidths.updatedDate ?? defaultColumnWidths.updatedDate,
                filterable: false,
                renderCell: (params) => {
                    if (params.value) {
                        return new Date(params.value).toLocaleString();
                    } else {
                        return "";
                    }
                },
            },
        ];
        disableSortingAndMenu(header);
        return header;
    }, []);

    const businessUnitCodes = useBusinessUnitCodes();

    const businessUnitOptions = useMemo(() => {
        if (businessUnitCodes.length > 1) {
            return [{id: "", name: "-- CLEAR --"}, ...businessUnitCodes];
        }
        return businessUnitCodes;
    }, [businessUnitCodes]);

    const scopedBusinessUnitCodes = useMemo(() => {
        if (filter.businessUnitCode) {
            return [filter.businessUnitCode];
        }
        return [];
    }, [filter.businessUnitCode]);

    const [facilities, totalRowCount, refetchFacilities] = useFacilities({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        businessUnitCodes: scopedBusinessUnitCodes,
    });
    const handleColumnResize = (params) => {
        setFacilitiesColumnWidths((prevWidths) => ({
            ...prevWidths,
            [params.colDef.field]: params.width,
        }));
    };
    const facilityModelRef = useRef();
    const onShowFacilityDetail = (facility) => {
        navigate(`/facilities/${facility.id}`);
    };
    const filterChangeFunctions = useMemo(
        () => ({
            businessUnitCode: (value) => {
                setFilter((prevState) => ({
                    ...prevState,
                    businessUnitCode: value,
                }));
            },
        }),
        []
    );
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
            </GridToolbarContainer>
        );
    }
    function renderControlPanel() {
        return (
            <Box pt={1}>
                <Grid
                    container
                    spacing={1}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Grid item xs={2}>
                        <FacilitiesFilter
                            options={businessUnitOptions}
                            filter={filter}
                            filterChangeFunctions={filterChangeFunctions}
                        />
                    </Grid>
                    <Grid
                        item
                        sx={{
                            display: "flex",
                            justifyContent: "right",
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => facilityModelRef.current.handleOpen(null)}
                        >
                            + New Facility
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    }
    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <PageHeader title="Manage Facilities" children={renderControlPanel()} />
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    <DataGrid
                        columns={columns}
                        rows={facilities}
                        paginationMode="server"
                        rowCount={totalRowCount}
                        disableColumnFilter={true}
                        onRowClick={({row}) => onShowFacilityDetail(row)}
                        pageSizeOptions={[10, 20, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        density="compact"
                        slots={{toolbar: CustomToolbar}}
                        slotProps={{
                            toolbar: {
                                csvOptions: {disableToolbarButton: true},
                                printOptions: {disableToolbarButton: true},
                                showQuickFilter: false,
                            },
                        }}
                        disableRowSelectionOnClick={true}
                        onColumnWidthChange={handleColumnResize}
                        sx={{
                            height: "100%",
                            position: "absolute",
                            width: "100%",
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },
                        }}
                    />
                </Box>
                <FacilityModal ref={facilityModelRef} refetchFacilities={refetchFacilities} />
            </Box>
        </>
    );
};

export default FacilitiesTable;
